/* eslint-disable react/prop-types */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import React, { useId, useMemo, useState } from 'react';
import { Button, Form } from 'react-bootstrap';

import { useAdvanceTableContext } from '../../../providers/AdvanceTableProvider';
import { useBreakpoints } from '../../../hooks/useBreakpoints';
import Flex from '../Flex';
import AdvanceTablePagination from './AdvanceTablePagination';
import { buildAdvanceTableMobileSortOptions, getAdvanceTableMobileSortValue, parseAdvanceTableMobileSortValue } from './advanceTableMobileSort';

export const AdvanceTableFooter = ({
	viewAllBtn,
	navButtons,
	pageNumberNav,
	className,
	rowInfo,
	rowsPerPageSelection,
	rowsPerPageOptions = [7, 15, 30, 50],
	centerPageNav = false,
	trailing = null,
}) => {
	const { breakpoints } = useBreakpoints();
	const isMobile = breakpoints.down('md');
	const {
		options,
		setPageSize,
		setPageIndex,
		setSorting,
		getAllColumns,
		previousPage,
		nextPage,
		getCanNextPage,
		getCanPreviousPage,
		getState,
		getPrePaginationRowModel,
		getPaginationRowModel,
		getRowModel,
	} = useAdvanceTableContext();

	const {
		pagination: { pageSize, pageIndex },
		sorting,
	} = getState();
	const [perPage] = useState(pageSize);
	const [isAllVisible, setIsAllVisible] = useState(false);
	const mobileSortSelectId = useId();
	const pageSizeSelectId = useId();
	const useSplitLayout = Boolean(centerPageNav || trailing);

	const sortOptions = useMemo(() => {
		if (!isMobile || options?.enableSorting === false) {
			return [];
		}
		try {
			return buildAdvanceTableMobileSortOptions({ getAllColumns });
		} catch {
			return [];
		}
	}, [isMobile, options?.enableSorting, getAllColumns]);

	const sortValue = useMemo(() => getAdvanceTableMobileSortValue(sorting, sortOptions), [sorting, sortOptions]);

	const handleSortChange = (e) => {
		setSorting(parseAdvanceTableMobileSortValue(e.target.value));
		setPageIndex(0);
	};

	const startControls = (
		<Flex alignItems='center' className='fs-10 advance-table-footer__start'>
			{rowInfo && (
				<p className='mb-0'>
					<span className='d-none d-sm-inline-block me-2'>
						{(() => {
							try {
								const visibleRows = options?.manualPagination ? getRowModel()?.rows : getPaginationRowModel()?.rows;
								const visibleCount = visibleRows?.length || 0;
								const total =
									typeof options?.rowCount === 'number' ? options.rowCount : getPrePaginationRowModel()?.rows?.length || 0;
								if (!total) return '0 to 0 of 0';
								const from = pageSize * pageIndex + 1;
								return `${from} to ${pageSize * pageIndex + visibleCount} of ${total}`;
							} catch (error) {
								return '0 to 0 of 0';
							}
						})()}
					</span>
					{viewAllBtn && (
						<>
							<span className='d-none d-sm-inline-block me-2'>&mdash;</span>
							<Button
								variant='link'
								size='sm'
								className='py-2 px-0 fw-semibold'
								onClick={() => {
									setIsAllVisible(!isAllVisible);
									try {
										const prePaginationModel = getPrePaginationRowModel();
										const totalRows = prePaginationModel?.rows?.length || 0;
										setPageSize(isAllVisible ? perPage : totalRows);
									} catch (error) {
										setPageSize(isAllVisible ? perPage : 0);
									}
								}}
							>
								View {isAllVisible ? 'less' : 'all'}
								<FontAwesomeIcon icon='chevron-right' className='ms-1 fs-11' />
							</Button>
						</>
					)}
				</p>
			)}
			{rowsPerPageSelection && (
				<>
					<Form.Label htmlFor={pageSizeSelectId} className='mb-0 mx-2 advance-table-footer__page-size-label'>
						每页显示条数：
					</Form.Label>
					<Form.Select
						id={pageSizeSelectId}
						name={pageSizeSelectId}
						size='sm'
						className='w-auto advance-table-footer__page-size-select'
						onChange={(e) => setPageSize(Number(e.target.value))}
						defaultValue={pageSize}
					>
						{rowsPerPageOptions.map((value) => (
							<option value={value} key={value}>
								{value}
							</option>
						))}
					</Form.Select>
				</>
			)}
		</Flex>
	);

	const pageNav = pageNumberNav ? (
		<div className='advance-table-footer__page-nav'>
			<AdvanceTablePagination />
		</div>
	) : null;

	const navButtonControls = navButtons ? (
		<Flex>
			<Button size='sm' variant={getCanPreviousPage() ? 'primary' : 'tertiary'} onClick={() => previousPage()} className={classNames({ disabled: !getCanPreviousPage() })}>
				上翻
			</Button>
			<Button
				size='sm'
				variant={getCanNextPage() ? 'primary' : 'tertiary'}
				className={classNames('ms-2', {
					disabled: !getCanNextPage(),
				})}
				onClick={() => nextPage()}
			>
				下翻
			</Button>
		</Flex>
	) : null;

	return (
		<div className={classNames(className, 'advance-table-footer-shell')}>
			{sortOptions.length > 0 ? (
				<>
					<Form.Label htmlFor={mobileSortSelectId} className='visually-hidden'>
						排序方式
					</Form.Label>
					<Form.Select
						id={mobileSortSelectId}
						name={mobileSortSelectId}
						size='sm'
						className='advance-table-footer__mobile-sort'
						value={sortValue}
						onChange={handleSortChange}
						aria-label='排序方式'
					>
						{sortOptions.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</Form.Select>
				</>
			) : null}
			{useSplitLayout ? (
				<div className='advance-table-footer advance-table-footer--split'>
					{startControls}
					<div className='advance-table-footer__center'>{trailing || pageNav}</div>
					<div className='advance-table-footer__end'>
						{trailing ? pageNav : null}
						{navButtonControls}
					</div>
				</div>
			) : (
				<Flex className='advance-table-footer align-items-center justify-content-between flex-wrap gap-2'>
					{startControls}
					{pageNav}
					{navButtonControls}
				</Flex>
			)}
		</div>
	);
};

export default AdvanceTableFooter;
